/**
 * TaskGanttChart.tsx
 * 
 * Componente per visualizzare una timeline Gantt dei task.
 * Mostra le barre temporali, durate, e stati dei task in formato Gantt.
 */

import React, { useMemo, useState } from 'react';
import {
  Card,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  Select,
  ActionIcon,
  Tooltip,
  Alert,
  Loader,
  Center,
  ScrollArea,
  Box,
} from '@mantine/core';
import {
  IconCalendar,
  IconZoomIn,
  IconZoomOut,
  IconCalendarTime,
  IconPlayerPlay,
  IconCheck,
  IconClock,
} from '@tabler/icons-react';
import { ProjectTask } from '../../types/api';

interface TaskGanttChartProps {
  tasks: ProjectTask[];
  isLoading?: boolean;
  onTaskClick?: (task: ProjectTask) => void;
  onTaskUpdate?: (taskId: string, updates: Partial<ProjectTask>) => void;
}

type ViewMode = 'days' | 'weeks' | 'months';

interface GanttTask {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  status: string;
  statusString?: string;
  assignedUser?: string;
  completionPercentage: number;
  dependencies?: string[];
}

const STATUS_COLORS = {
  'ToDo': '#1971c2',      // blue
  'InProgress': '#f59f00', // orange
  'Doing': '#f59f00',     // orange
  'Testing': '#9c36b5',   // violet
  'Done': '#2f9e44',      // green
  'Cancelled': '#e03131',  // red
  'Ideas': '#6741d9',     // purple
} as const;

const GANTT_CONFIG = {
  rowHeight: 40,
  headerHeight: 60,
  dayWidth: 30,
  weekWidth: 50,
  monthWidth: 100,
  padding: 20,
};

export function TaskGanttChart({ 
  tasks = [], 
  isLoading = false, 
  onTaskClick,
  onTaskUpdate 
}: TaskGanttChartProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('weeks');
  const [zoom, setZoom] = useState(1);

  // Prepara i dati per il Gantt
  const ganttData = useMemo(() => {
    if (!tasks.length) return { ganttTasks: [], startDate: new Date(), endDate: new Date() };

    // Filtra solo i task che hanno date valide
    const validTasks = tasks.filter(task => 
      (task.startDate || task.createdAt) && (task.endDate || task.dueDate)
    );

    if (validTasks.length === 0) {
      return { ganttTasks: [], startDate: new Date(), endDate: new Date() };
    }

    const ganttTasks: GanttTask[] = validTasks.map(task => {
      // Usa startDate se disponibile, altrimenti createdAt
      const startDate = task.startDate 
        ? new Date(task.startDate) 
        : new Date(task.createdAt);
      
      // Usa endDate se disponibile, altrimenti dueDate, altrimenti startDate + 1 giorno
      let endDate: Date;
      if (task.endDate) {
        endDate = new Date(task.endDate);
      } else if (task.dueDate) {
        endDate = new Date(task.dueDate);
      } else {
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
      }

      // Assicurati che endDate sia dopo startDate
      if (endDate <= startDate) {
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
      }

      return {
        id: task.id,
        name: task.name || task.title || 'Task senza nome',
        startDate,
        endDate,
        status: task.status?.toString() || 'ToDo',
        statusString: task.statusString,
        // Preferisce gli assegnatari multipli (task.assignees) quando presenti, unendo i
        // nomi; ricade sul vecchio campo singolo assignedToUser solo se assente.
        assignedUser:
          task.assignees && task.assignees.length > 0
            ? task.assignees.map((a) => a.fullName).join(', ')
            : task.assignedToUser?.fullName || `Utente ${task.assignedToUserId || '?'}`,
        completionPercentage: task.completionPercentage || 0,
      };
    });

    // Calcola il range temporale totale
    const allDates = ganttTasks.flatMap(task => [task.startDate, task.endDate]);
    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));

    // Aggiungi padding al range
    minDate.setDate(minDate.getDate() - 3);
    maxDate.setDate(maxDate.getDate() + 3);

    return {
      ganttTasks: ganttTasks.sort((a, b) => a.startDate.getTime() - b.startDate.getTime()),
      startDate: minDate,
      endDate: maxDate,
    };
  }, [tasks]);

  // Calcola dimensioni timeline
  const timelineDimensions = useMemo(() => {
    const { startDate, endDate } = ganttData;
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    let unitWidth: number;
    let units: { date: Date; label: string; isMainUnit: boolean }[] = [];
    
    switch (viewMode) {
      case 'days':
        unitWidth = GANTT_CONFIG.dayWidth * zoom;
        // Genera giorni
        for (let i = 0; i <= diffDays; i++) {
          const date = new Date(startDate);
          date.setDate(date.getDate() + i);
          units.push({
            date,
            label: date.getDate().toString(),
            isMainUnit: date.getDay() === 1, // Lunedì come main unit
          });
        }
        break;
        
      case 'weeks':
        unitWidth = GANTT_CONFIG.weekWidth * zoom;
        // Genera settimane
        const diffWeeks = Math.ceil(diffDays / 7);
        for (let i = 0; i <= diffWeeks; i++) {
          const date = new Date(startDate);
          date.setDate(date.getDate() + (i * 7));
          units.push({
            date,
            label: `W${getWeekNumber(date)}`,
            isMainUnit: i % 2 === 0,
          });
        }
        break;
        
      case 'months':
        unitWidth = GANTT_CONFIG.monthWidth * zoom;
        // Genera mesi
        const startMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        const currentMonth = new Date(startMonth);
        
        while (currentMonth <= endMonth) {
          units.push({
            date: new Date(currentMonth),
            label: currentMonth.toLocaleDateString('it-IT', { month: 'short' }),
            isMainUnit: currentMonth.getMonth() % 3 === 0,
          });
          currentMonth.setMonth(currentMonth.getMonth() + 1);
        }
        break;
    }
    
    return {
      unitWidth,
      totalWidth: units.length * unitWidth,
      units,
    };
  }, [ganttData, viewMode, zoom]);

  // Genera timeline header
  const renderTimelineHeader = () => {
    const { units, unitWidth } = timelineDimensions;
    
    return (
      <div 
        style={{ 
          height: GANTT_CONFIG.headerHeight,
          display: 'flex',
          borderBottom: '2px solid var(--mantine-color-gray-3)',
          backgroundColor: 'var(--mantine-color-gray-0)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        {units.map((unit, index) => (
          <div
            key={index}
            style={{
              width: unitWidth,
              height: '100%',
              borderRight: '1px solid var(--mantine-color-gray-3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: unit.isMainUnit ? 600 : 400,
              backgroundColor: unit.isMainUnit ? 'var(--mantine-color-blue-0)' : 'transparent',
            }}
          >
            {unit.label}
          </div>
        ))}
      </div>
    );
  };

  // Calcola posizione e larghezza barra task
  const getTaskBarDimensions = (task: GanttTask) => {
    const { startDate, endDate } = ganttData;
    const { unitWidth, totalWidth } = timelineDimensions;
    
    const taskStart = task.startDate.getTime();
    const taskEnd = task.endDate.getTime();
    const timelineStart = startDate.getTime();
    const timelineEnd = endDate.getTime();
    const timelineSpan = timelineEnd - timelineStart;
    
    const startPercent = (taskStart - timelineStart) / timelineSpan;
    const endPercent = (taskEnd - timelineStart) / timelineSpan;
    
    return {
      left: startPercent * totalWidth,
      width: (endPercent - startPercent) * totalWidth,
    };
  };

  // Render singola riga task
  const renderTaskRow = (task: GanttTask, index: number) => {
    const dimensions = getTaskBarDimensions(task);
    const statusKey = task.statusString || task.status || 'ToDo';
    const color = STATUS_COLORS[statusKey as keyof typeof STATUS_COLORS] || STATUS_COLORS.ToDo;
    
    return (
      <div
        key={task.id}
        style={{
          height: GANTT_CONFIG.rowHeight,
          borderBottom: '1px solid var(--mantine-color-gray-2)',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Barra del task */}
        <div
          style={{
            position: 'absolute',
            left: dimensions.left,
            width: Math.max(dimensions.width, 20), // Minimo 20px
            height: 24,
            backgroundColor: color,
            borderRadius: 4,
            cursor: 'pointer',
            opacity: statusKey === 'Done' ? 0.7 : 1,
            border: `2px solid ${color}`,
            overflow: 'hidden',
          }}
        >
          {/* Barra progresso se disponibile */}
          {task.completionPercentage > 0 && (
            <div
              style={{
                height: '100%',
                width: `${task.completionPercentage}%`,
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                transition: 'width 0.3s ease',
              }}
            />
          )}
          
          {/* Testo task se abbastanza largo */}
          {dimensions.width > 80 && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: 8,
                transform: 'translateY(-50%)',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 500,
                maxWidth: dimensions.width - 16,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {task.name}
            </div>
          )}
        </div>

        {/* Tooltip con dettagli - Area cliccabile */}
        <Tooltip
          label={
            <Box p="xs" style={{ 
              backgroundColor: '#1a1b1e', 
              color: 'white', 
              borderRadius: '4px',
              border: '1px solid #373a40'
            }}>
              <Text size="sm" fw={600} c="white" mb={4}>
                {task.name}
              </Text>
              <Text size="xs" c="gray.3" mb={2}>
                Stato: <Text span c="white">{statusKey}</Text>
              </Text>
              <Text size="xs" c="gray.3" mb={2}>
                Assegnato: <Text span c="white">{task.assignedUser}</Text>
              </Text>
              <Text size="xs" c="gray.3" mb={2}>
                Dal: <Text span c="white">{task.startDate.toLocaleDateString()}</Text>
              </Text>
              <Text size="xs" c="gray.3" mb={2}>
                Al: <Text span c="white">{task.endDate.toLocaleDateString()}</Text>
              </Text>
              {task.completionPercentage > 0 && (
                <Text size="xs" c="gray.3">
                  Progresso: <Text span c="white">{task.completionPercentage}%</Text>
                </Text>
              )}
            </Box>
          }
          position="top"
          withArrow
          styles={{
            tooltip: {
              backgroundColor: '#1a1b1e',
              border: '1px solid #373a40',
              color: 'white',
            },
            arrow: {
              borderColor: '#373a40',
            }
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: dimensions.left,
              width: Math.max(dimensions.width, 20),
              height: 24,
              cursor: 'pointer',
              zIndex: 1,
            }}
            onClick={(e) => {
              e.stopPropagation();
              const originalTask = tasks.find(t => t.id === task.id);
              if (originalTask) {
                onTaskClick?.(originalTask);
              }
            }}
          />
        </Tooltip>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <Center h={200}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text>Caricamento Gantt chart...</Text>
          </Stack>
        </Center>
      </Card>
    );
  }

  if (ganttData.ganttTasks.length === 0) {
    return (
      <Card>
        <Title order={4} mb="md">Timeline Gantt</Title>
        <Alert
          icon={<IconCalendarTime size="1rem" />}
          title="Nessun task con date"
          color="gray"
        >
          Non ci sono task con date valide per visualizzare il Gantt chart.
          Aggiungi date di inizio e fine ai task per visualizzarli nella timeline.
        </Alert>
      </Card>
    );
  }

  return (
    <Card>
      <Stack gap="md">
        {/* Header controlli */}
        <Group justify="space-between">
          <Title order={4}>Timeline Gantt</Title>
          <Group gap="xs">
            <Select
              data={[
                { value: 'days', label: 'Giorni' },
                { value: 'weeks', label: 'Settimane' },
                { value: 'months', label: 'Mesi' },
              ]}
              value={viewMode}
              onChange={(value) => setViewMode(value as ViewMode)}
              size="sm"
              w={120}
            />
            <ActionIcon
              variant="subtle"
              onClick={() => setZoom(Math.min(zoom * 1.2, 3))}
              disabled={zoom >= 3}
            >
              <IconZoomIn size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              onClick={() => setZoom(Math.max(zoom / 1.2, 0.3))}
              disabled={zoom <= 0.3}
            >
              <IconZoomOut size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Legenda */}
        <Group gap="md">
          <Text size="sm" fw={500}>Legenda:</Text>
          {Object.entries(STATUS_COLORS).map(([status, color]) => (
            <Group key={status} gap={4}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: color,
                  borderRadius: 2,
                }}
              />
              <Text size="xs">{status}</Text>
            </Group>
          ))}
        </Group>

        {/* Gantt Chart */}
        <ScrollArea h={Math.min(600, ganttData.ganttTasks.length * GANTT_CONFIG.rowHeight + GANTT_CONFIG.headerHeight)}>
          <Box style={{ minWidth: timelineDimensions.totalWidth + GANTT_CONFIG.padding * 2 }}>
            {/* Header timeline */}
            {renderTimelineHeader()}
            
            {/* Righe task */}
            <div style={{ padding: `0 ${GANTT_CONFIG.padding}px` }}>
              {ganttData.ganttTasks.map((task, index) => renderTaskRow(task, index))}
            </div>
          </Box>
        </ScrollArea>

        {/* Info footer */}
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {ganttData.ganttTasks.length} task • 
            Dal {ganttData.startDate.toLocaleDateString()} al {ganttData.endDate.toLocaleDateString()}
          </Text>
          <Text size="sm" c="dimmed">
            Clicca sulle barre per modificare i task
          </Text>
        </Group>
      </Stack>
    </Card>
  );
}

// Helper function per calcolare numero settimana
function getWeekNumber(date: Date): number {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}

export default TaskGanttChart;